import { Component, ReactNode } from "react";
import { track, Events, hashStack, sanitizeStackTop, sanitizeComponentStack } from "../lib/analytics";
import { captureError } from "../lib/captureError";
import { ServerError } from "../pages/ServerError";

interface Props { children: ReactNode; fallback?: ReactNode; }
interface State { hasError: boolean; error?: Error; errorHash?: string; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };
  static getDerivedStateFromError(error: Error): State { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: any) {
    if (import.meta.env.DEV) console.error("ErrorBoundary caught:", error, info);
    // Report to PostHog so we get production observability without a separate Sentry account.
    // Stack traces include hashed chunk names (`index-AbC123.js`) that rotate every build,
    // so sending raw stacks inflates property cardinality without bound. Instead, send:
    //   - stack_hash: 8-char sha256 of the stack (low cardinality, joinable)
    //   - stack_top:  first 200 chars only (function name + source line is enough to triage)
    //   - componentStack: 500 chars w/ chunk hashes + line numbers stripped
    // Sentry already has the raw payload via captureError below — no loss of fidelity.
    let stackHash: string | undefined;
    try {
      const stack = error.stack || '';
      const componentStack = String(info?.componentStack || '');
      stackHash = hashStack(stack);
      track(Events.ERROR_BOUNDARY, {
        message: error.message.slice(0, 500),
        stack_hash: stackHash,
        stack_top: sanitizeStackTop(stack),
        componentStack: sanitizeComponentStack(componentStack),
      });
    } catch {}
    // Stash hash on state so the fallback ServerError can surface it for support.
    if (stackHash) {
      this.setState({ errorHash: stackHash });
    }
    // Mirror to Sentry for stack-trace-grade triage alongside PostHog product analytics
    captureError(error, {
      scope: "ErrorBoundary",
      breadcrumb: "React render threw — fallback UI shown",
      extra: { componentStack: String(info?.componentStack || '').slice(0, 2000) },
    });
  }
  /** Reset so React re-mounts the children and tries to render again. */
  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorHash: undefined });
  };
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <ServerError errorHash={this.state.errorHash} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
